import { Component, Inject, OnInit, Pipe, PipeTransform } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ParticipantService, PARTNER_ID_TOKEN, SubjectParticipant } from '@galaxy/conversation/core';
import { FeatureFlagService, FeatureFlagStatusInterface } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { ConversationChannel, GlobalParticipantType, Participant } from '@vendasta/conversation';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { IAMService, ListAuxiliaryDataResponse } from '@vendasta/iamv2';
import { PhoneNumberFormatPipe } from '@vendasta/uikit';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AccessService, Feature, StubAccessService } from '../../core/access';
import { SalesActivityApiService } from '../../sales/sales-activity/sales-activity-api.service';
import { AssignedSalespersonService } from '../assigned-salesperson.service';

const DEFAULT_IMAGE_URL =
  'https://lh3.googleusercontent.com/70DGcv8fyAzy1n_cWh8GyVbsCIWzPGZMdBFiwJa8Ot-Ss5Rf_oZY8hKOG2wAttY4_iuU5TlO9NLlDrBGuEJBj6DHwRnGd2CJ32J2Im3c1g';

@Component({
  templateUrl: './assigned-salesperson-dialog.component.html',
  styleUrls: ['./assigned-salesperson-dialog.component.scss'],
})
export class AssignedSalespersonDialogComponent implements OnInit {
  name: string;
  title: string;
  tel: string;
  imgUrl: string;
  countryCode: string;
  private readonly salespersonId: string;
  private readonly businessId: string;

  sending = false;
  buttonEnabled = false;
  message = '';

  bookMeetingUrl$: Observable<string>;
  awayMessage$: Observable<string>;

  canAccessInbox$: Observable<boolean>;
  subjectParticipants$: Observable<SubjectParticipant[]>;
  currentIAMParticipant$ = this.participantService.buildIAMUserParticipant() as Observable<Participant>;
  readonly conversationChannel = ConversationChannel.CONVERSATION_CHANNEL_INTERNAL;

  constructor(
    private dialogRef: MatDialogRef<AssignedSalespersonDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    data: {
      imgUrl: string;
      name: string;
      title: string;
      telephone: string;
      country: string;
      salespersonId: string;
      businessId: string;
    },
    @Inject(PARTNER_ID_TOKEN) readonly partnerId$: Observable<string>,
    private salesActivityService: SalesActivityApiService,
    private assignedSalespersonService: AssignedSalespersonService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private featureFlagService: FeatureFlagService,
    private participantService: ParticipantService,
    private readonly userService: IAMService,
    @Inject(StubAccessService) private accessService: AccessService,
  ) {
    this.name = data.name;
    this.title = data.title;
    this.imgUrl = data.imgUrl ? data.imgUrl : DEFAULT_IMAGE_URL;
    this.tel = data.telephone;
    this.salespersonId = data.salespersonId;
    this.businessId = data.businessId;
    this.countryCode = data.country;
  }

  ngOnInit(): void {
    this.bookMeetingUrl$ = this.assignedSalespersonService.bookMeetingUrl$;
    this.awayMessage$ = this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.userService.listUserAuxiliaryData(
          {
            partnerId: partnerId,
            objectId: this.salespersonId,
          },
          {
            cursor: '',
            pageSize: 100,
          },
        );
      }),
      map((resp: ListAuxiliaryDataResponse) => {
        let awayMessage = '';
        const objectSchema = JSON.parse(resp.jsonSchema);
        const objectData = JSON.parse(resp.jsonData);
        for (const property in objectSchema.properties) {
          if (objectSchema.properties[property].title === 'Away Message') {
            awayMessage = <string>objectData[property];
            break;
          }
        }
        return awayMessage;
      }),
    );

    this.canAccessInbox$ = combineLatest([
      this.partnerId$,
      this.accessService.hasAccessToFeature(Feature.inboxSalesContactUs),
    ]).pipe(
      switchMap(([partnerId, hasAccess]) =>
        hasAccess
          ? this.featureFlagService.batchGetStatus(partnerId, '', ['inbox_tab_pcc'])
          : of(<FeatureFlagStatusInterface>{}),
      ),
      map((featureFlagStatus) => featureFlagStatus['inbox_tab_pcc'] ?? false),
      shareReplay(1),
    );

    this.subjectParticipants$ = this.partnerId$.pipe(
      map((partnerId) => [
        new SubjectParticipant({
          internalParticipantId: partnerId,
          participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
        }),
        new SubjectParticipant({
          internalParticipantId: 'VMF',
          participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER,
        }),
      ]),
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  enableSend(): void {
    this.buttonEnabled = !!this.message;
  }

  send(): void {
    if (this.message) {
      this.sending = true;
      this.salesActivityService
        .createContactUsInboundMessageSalesActivity(this.businessId, this.salespersonId, this.message)
        .pipe(take(1))
        .subscribe(
          () => {
            this.dialogRef.close();
            this.sending = false;
          },
          () => {
            this.sending = false;
            this.snackbarService.errorSnack(this.translateService.instant('CONTACT_SALESPERSON.ERROR'));
          },
        );
    }
  }
}

@Pipe({ name: 'formatPhoneNumber' })
export class FormatPhoneNumberPipe implements PipeTransform {
  formatPhoneNumber: PhoneNumberFormatPipe = new PhoneNumberFormatPipe();

  transform(phoneNumber: string, country: string): string {
    return this.formatPhoneNumber.transform(phoneNumber, country);
  }
}
